import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Switch,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tabs,
  Tab
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  CloudSync as SyncIcon,
  Preview as PreviewIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Inventory as ProductIcon,
  Build as InstallationIcon,
  Description as ConditionsIcon
} from '@mui/icons-material';
import { apiService } from '../../services/apiService';

interface ValidationResult {
  valid: boolean;
  error?: string;
  found_columns?: string[];
  unknown_columns?: string[];
  total_rows?: number;
}

interface PreviewResult {
  total_found: number;
  preview: any[];
}

interface SyncResult {
  success: boolean;
  error?: string;
  stats: {
    total: number;
    created: number;
    updated: number;
    deactivated: number;
    errors: number;
    error_details?: Array<{ codigo: string; error: string }>;
  };
  dry_run: boolean;
}

interface AuthStatus {
  authenticated: boolean;
  error?: string;
  credentials_path?: string;
}

// Interfaces para instalaciones
interface InstallationValidationResult {
  valid: boolean;
  found_headers?: Record<string, number>;
  missing_headers?: string[];
  total_rows?: number;
  headers?: string[];
  error?: string;
}

interface InstallationPreviewResult {
  total_data_rows: number;
  preview: any[];
  headers: string[];
}

interface InstallationSyncResult {
  processed: number;
  created: number;
  updated: number;
  deactivated?: number;
  errors: string[];
  dry_run?: boolean;
}

// Interfaz para el resultado del preview (dry_run=true)
interface InstallationPreviewSyncResult {
  dry_run: boolean;
  total_rows_found: number;
  total_rows_with_trabajo: number;
  processed: number;
  created: number;
  updated: number;
  deactivated?: number;
  errors: string[];
  message: string;
}

interface AppSetting {
  key: string;
  value: string;
  category: string;
}

interface Conditions {
  condiciones_generales_default: string;
  condiciones_adicionales_default: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const GoogleSheetsSync: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  
  // Estados para productos (se cargarán de la BD)
  const [spreadsheetId, setSpreadsheetId] = useState('');
  const [rangeName, setRangeName] = useState('');
  const [headerRow, setHeaderRow] = useState(0);
  const [dryRun, setDryRun] = useState(true);
  
  const [loading, setLoading] = useState(false);
  const [validation, setValidation] = useState<ValidationResult | null>(null);
  const [preview, setPreview] = useState<PreviewResult | null>(null);
  const [syncResult, setSyncResult] = useState<SyncResult | null>(null);
  const [authStatus, setAuthStatus] = useState<AuthStatus | null>(null);

  // Estados para instalaciones (se cargarán de la BD)
  const [installationSpreadsheetId, setInstallationSpreadsheetId] = useState('');
  const [installationRangeName, setInstallationRangeName] = useState('');
  const [installationHeaderRow, setInstallationHeaderRow] = useState(0);
  const [installationDryRun, setInstallationDryRun] = useState(true);
  const [installationValidation, setInstallationValidation] = useState<InstallationValidationResult | null>(null);
  const [installationPreview, setInstallationPreview] = useState<InstallationPreviewResult | null>(null);
  const [installationSyncResult, setInstallationSyncResult] = useState<InstallationSyncResult | null>(null);

  // Estados para condiciones
  const [conditions, setConditions] = useState<Conditions>({
    condiciones_generales_default: '',
    condiciones_adicionales_default: ''
  });
  const [conditionsLoading, setConditionsLoading] = useState(false);
  const [conditionsSaved, setConditionsSaved] = useState(false);

  // Cargar settings desde la BD
  const loadSettings = async () => {
    try {
      console.log('Loading settings from database...');
      const settings = await apiService.get<AppSetting[]>('/settings/');
      console.log('Settings loaded:', settings);
      
      // Cargar settings de productos
      const productsSpreadsheetId = settings.find((s: AppSetting) => s.key === 'products_spreadsheet_id' && s.category === 'google_sheets')?.value || '';
      const productsRangeName = settings.find((s: AppSetting) => s.key === 'products_range_name' && s.category === 'google_sheets')?.value || '';
      const productsHeaderRow = parseInt(settings.find((s: AppSetting) => s.key === 'products_header_row' && s.category === 'google_sheets')?.value || '0');
      
      console.log('Products settings:', { productsSpreadsheetId, productsRangeName, productsHeaderRow });
      
      setSpreadsheetId(productsSpreadsheetId);
      setRangeName(productsRangeName);
      setHeaderRow(productsHeaderRow);
      
      // Cargar settings de instalaciones
      const installationsSpreadsheetId = settings.find((s: AppSetting) => s.key === 'installations_spreadsheet_id' && s.category === 'google_sheets')?.value || '';
      const installationsRangeName = settings.find((s: AppSetting) => s.key === 'installations_range_name' && s.category === 'google_sheets')?.value || '';
      const installationsHeaderRow = parseInt(settings.find((s: AppSetting) => s.key === 'installations_header_row' && s.category === 'google_sheets')?.value || '0');
      
      console.log('Installations settings:', { installationsSpreadsheetId, installationsRangeName, installationsHeaderRow });
      
      setInstallationSpreadsheetId(installationsSpreadsheetId);
      setInstallationRangeName(installationsRangeName);
      setInstallationHeaderRow(installationsHeaderRow);
      
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  };

  // Guardar un setting individual (upsert)
  const saveSetting = async (key: string, value: string, category: string = 'google_sheets') => {
    try {
      console.log('Saving setting:', { key, value, category });
      
      // Intentar actualizar primero
      try {
        await apiService.put(`/settings/${key}`, {
          value,
          category
        });
        console.log('Setting updated successfully');
      } catch (updateError: any) {
        // Si falla porque no existe (404), crear nuevo
        if (updateError.response?.status === 404) {
          await apiService.post('/settings/', {
            key,
            value,
            category
          });
          console.log('Setting created successfully');
        } else {
          throw updateError;
        }
      }
    } catch (error) {
      console.error('Error saving setting:', error);
    }
  };

  // Cargar settings al montar el componente
  useEffect(() => {
    loadSettings();
    loadConditions();
  }, []);

  // Cargar condiciones desde el backend
  const loadConditions = async () => {
    try {
      setConditionsLoading(true);
      const data = await apiService.get<Conditions>('/app-settings/conditions');
      setConditions(data);
      setConditionsSaved(false);
    } catch (error) {
      console.error('Error loading conditions:', error);
    } finally {
      setConditionsLoading(false);
    }
  };

  // Guardar condiciones en el backend
  const saveConditions = async () => {
    try {
      setConditionsLoading(true);
      await apiService.put('/app-settings/conditions', conditions);
      setConditionsSaved(true);
      setTimeout(() => setConditionsSaved(false), 3000);
    } catch (error) {
      console.error('Error saving conditions:', error);
      alert('Error al guardar las condiciones');
    } finally {
      setConditionsLoading(false);
    }
  };

  // Handlers que actualizan tanto el estado local como la BD
  const handleSpreadsheetIdChange = (value: string) => {
    console.log('Spreadsheet ID changed:', value);
    setSpreadsheetId(value);
    saveSetting('products_spreadsheet_id', value);
  };

  const handleRangeNameChange = (value: string) => {
    console.log('Range name changed:', value);
    setRangeName(value);
    saveSetting('products_range_name', value);
  };

  const handleHeaderRowChange = (value: number) => {
    console.log('Header row changed:', value);
    setHeaderRow(value);
    saveSetting('products_header_row', value.toString());
  };

  const handleInstallationSpreadsheetIdChange = (value: string) => {
    setInstallationSpreadsheetId(value);
    saveSetting('installations_spreadsheet_id', value);
  };

  const handleInstallationRangeNameChange = (value: string) => {
    setInstallationRangeName(value);
    saveSetting('installations_range_name', value);
  };

  const handleInstallationHeaderRowChange = (value: number) => {
    setInstallationHeaderRow(value);
    saveSetting('installations_header_row', value.toString());
  };

  const checkAuthStatus = async () => {
    try {
      setLoading(true);
      const response = await apiService.get<AuthStatus>('/google-sheets/auth-status');
      setAuthStatus(response);
    } catch (error) {
      console.error('Error checking auth status:', error);
      setAuthStatus({
        authenticated: false,
        error: 'Error al verificar autenticación'
      });
    } finally {
      setLoading(false);
    }
  };

  const validateSheet = async () => {
    if (!spreadsheetId.trim()) {
      alert('Por favor ingresa el ID de Google Sheets');
      return;
    }

    try {
      setLoading(true);
      setValidation(null);
      setPreview(null);
      setSyncResult(null);

      const response = await apiService.get<ValidationResult>('/google-sheets/validate-sheet', {
        params: {
          spreadsheet_id: spreadsheetId,
          range_name: rangeName,
          header_row: headerRow
        }
      });

      setValidation(response);
    } catch (error: any) {
      setValidation({
        valid: false,
        error: error.response?.data?.detail || 'Error al validar la hoja'
      });
    } finally {
      setLoading(false);
    }
  };

  const previewProducts = async () => {
    if (!validation?.valid) {
      alert('Primero valida la estructura de la hoja');
      return;
    }

    try {
      setLoading(true);
      setPreview(null);

      const response = await apiService.get<PreviewResult>('/google-sheets/preview-products', {
        params: {
          spreadsheet_id: spreadsheetId,
          range_name: rangeName,
          header_row: headerRow,
          limit: 10
        }
      });

      setPreview(response);
    } catch (error: any) {
      console.error('Error previewing products:', error);
      alert(error.response?.data?.detail || 'Error al previsualizar productos');
    } finally {
      setLoading(false);
    }
  };

  const syncProducts = async () => {
    if (!validation?.valid) {
      alert('Primero valida la estructura de la hoja');
      return;
    }

    if (!dryRun && !window.confirm('¿Estás seguro de que quieres sincronizar los productos? Esta acción modificará la base de datos.')) {
      return;
    }

    try {
      setLoading(true);
      setSyncResult(null);

      const response = await apiService.post<SyncResult>('/google-sheets/sync-products', null, {
        params: {
          spreadsheet_id: spreadsheetId,
          range_name: rangeName,
          header_row: headerRow,
          dry_run: dryRun
        }
      });

      setSyncResult(response);
    } catch (error: any) {
      setSyncResult({
        success: false,
        error: error.response?.data?.detail || 'Error al sincronizar productos',
        stats: { total: 0, created: 0, updated: 0, deactivated: 0, errors: 0 },
        dry_run: dryRun
      });
    } finally {
      setLoading(false);
    }
  };

  // Funciones para instalaciones
  const validateInstallationSheet = async () => {
    if (!installationSpreadsheetId.trim()) {
      alert('Por favor ingresa el ID de Google Sheets');
      return;
    }

    try {
      setLoading(true);
      setInstallationValidation(null);
      setInstallationPreview(null);
      setInstallationSyncResult(null);

      const response = await apiService.get<InstallationValidationResult>('/google-sheets/validate-installation-sheet', {
        params: {
          spreadsheet_id: installationSpreadsheetId,
          range_name: installationRangeName,
          header_row: installationHeaderRow
        }
      });

      setInstallationValidation(response);
    } catch (error: any) {
      setInstallationValidation({
        valid: false,
        error: error.response?.data?.detail || 'Error al validar la hoja de instalaciones'
      });
    } finally {
      setLoading(false);
    }
  };

  const previewInstallations = async () => {
    if (!installationValidation?.valid) {
      alert('Primero valida la estructura de la hoja');
      return;
    }

    try {
      setLoading(true);
      setInstallationPreview(null);

      const response = await apiService.get<InstallationPreviewResult>('/google-sheets/preview-installations', {
        params: {
          spreadsheet_id: installationSpreadsheetId,
          range_name: installationRangeName,
          header_row: installationHeaderRow,
          limit: 10
        }
      });

      setInstallationPreview(response);
    } catch (error: any) {
      console.error('Error previewing installations:', error);
      alert(error.response?.data?.detail || 'Error al previsualizar instalaciones');
    } finally {
      setLoading(false);
    }
  };

  const syncInstallations = async () => {
    console.log('syncInstallations called with dry_run:', installationDryRun);
    
    if (!installationValidation?.valid) {
      alert('Primero valida la estructura de la hoja');
      return;
    }

    if (!installationDryRun && !window.confirm('¿Estás seguro de que quieres sincronizar las instalaciones? Esta acción modificará la base de datos.')) {
      return;
    }

    try {
      setLoading(true);
      setInstallationSyncResult(null);
      
      console.log('Calling sync-installations API with params:', {
        spreadsheet_id: installationSpreadsheetId,
        range_name: installationRangeName,
        header_row: installationHeaderRow,
        dry_run: installationDryRun
      });

      const response = await apiService.post<InstallationSyncResult | InstallationPreviewSyncResult>('/google-sheets/sync-installations', null, {
        params: {
          spreadsheet_id: installationSpreadsheetId,
          range_name: installationRangeName,
          header_row: installationHeaderRow,
          dry_run: installationDryRun
        }
      });

      console.log('Sync response:', response);
      
      // Manejar diferentes formatos de respuesta
      if ('dry_run' in response && response.dry_run) {
        // Es un preview (dry_run=true)
        const previewResponse = response as InstallationPreviewSyncResult;
        setInstallationSyncResult({
          processed: previewResponse.processed,
          created: previewResponse.created,
          updated: previewResponse.updated,
          errors: previewResponse.errors,
          dry_run: true
        });
      } else {
        // Es sincronización real
        setInstallationSyncResult(response as InstallationSyncResult);
      }
    } catch (error: any) {
      console.error('Error in syncInstallations:', error);
      setInstallationSyncResult({
        processed: 0,
        created: 0,
        updated: 0,
        errors: [error.response?.data?.detail || 'Error al sincronizar instalaciones']
      });
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    checkAuthStatus();
  }, []);

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        Sincronización con Google Sheets
      </Typography>
      
      <Typography variant="body1" color="text.secondary" paragraph>
        Importa productos y servicios de instalación desde hojas de cálculo de Google Sheets
      </Typography>

      {/* Estado de autenticación */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={2}>
            <Typography variant="h6">Estado de Autenticación</Typography>
            <Button onClick={checkAuthStatus} disabled={loading} size="small">
              Verificar
            </Button>
          </Box>
          
          {authStatus && (
            <Box mt={2}>
              {authStatus.authenticated ? (
                <Alert severity="success" icon={<CheckIcon />}>
                  Autenticación configurada correctamente
                </Alert>
              ) : (
                <Alert severity="error" icon={<ErrorIcon />}>
                  {authStatus.error || 'No autenticado'}
                </Alert>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Pestañas */}
      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={tabValue} 
            onChange={(_, newValue) => setTabValue(newValue)}
            aria-label="sync tabs"
          >
            <Tab 
              icon={<ProductIcon />} 
              label="Sincronizar Productos" 
              id="tab-0"
              aria-controls="tabpanel-0"
            />
            <Tab 
              icon={<InstallationIcon />} 
              label="Sincronizar Instalaciones" 
              id="tab-1"
              aria-controls="tabpanel-1"
            />
            <Tab 
              icon={<ConditionsIcon />} 
              label="Condiciones" 
              id="tab-2"
              aria-controls="tabpanel-2"
            />
          </Tabs>
        </Box>

        {/* Panel de Productos */}
        <TabPanel value={tabValue} index={0}>
          {/* Configuración productos */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Configuración de la Hoja de Productos
              </Typography>
              
              <Box display="flex" flexDirection="column" gap={2}>
            <TextField
              label="ID de Google Sheets"
              value={spreadsheetId}
              onChange={(e) => handleSpreadsheetIdChange(e.target.value)}
              fullWidth
              placeholder="1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgvE2upms"
              helperText="El ID se encuentra en la URL: https://docs.google.com/spreadsheets/d/[ID]/edit"
            />
            
            <TextField
              label="Rango"
              value={rangeName}
              onChange={(e) => handleRangeNameChange(e.target.value)}
              placeholder="'Lista para IA'!A1:M260"
              helperText="Ejemplo: 'Lista para IA'!A1:M260 (usar comillas simples si el nombre tiene espacios)"
            />
            
            <TextField
              label="Fila de encabezados"
              type="number"
              value={headerRow}
              onChange={(e) => handleHeaderRowChange(parseInt(e.target.value) || 0)}
              helperText="Número de fila que contiene los nombres de columnas (empezando desde 0)"
            />
          </Box>
        </CardContent>
      </Card>


      {/* Acciones */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Acciones
          </Typography>
          <Box display="flex" gap={2} flexWrap="wrap">
            <Button
              variant="outlined"
              startIcon={<CheckIcon />}
              onClick={validateSheet}
              disabled={loading || !authStatus?.authenticated}
            >
              1. Validar Estructura
            </Button>
            <Button
              variant="outlined"
              startIcon={<PreviewIcon />}
              onClick={previewProducts}
              disabled={loading || !validation?.valid}
            >
              2. Previsualizar
            </Button>
            <Button
              variant="contained"
              startIcon={<SyncIcon />}
              onClick={syncProducts}
              disabled={loading || !validation?.valid}
              color={dryRun ? "primary" : "warning"}
            >
              3. {dryRun ? 'Simular' : 'Sincronizar'}
            </Button>
          </Box>
          <Box mt={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={dryRun}
                  onChange={(e) => setDryRun(e.target.checked)}
                />
              }
              label="Modo de prueba (no modifica la base de datos)"
            />
          </Box>
          {loading && (
            <Box display="flex" alignItems="center" gap={1} mt={2}>
              <CircularProgress size={20} />
              <Typography variant="body2">Procesando...</Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Resultados de sincronización (ahora justo debajo de Acciones) */}
      {syncResult && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Resultado de {syncResult.dry_run ? 'Simulación' : 'Sincronización'}
            </Typography>
            {syncResult.success ? (
              <Alert severity="success">
                {syncResult.dry_run ? 'Simulación completada' : 'Sincronización exitosa'}
              </Alert>
            ) : (
              <Alert severity="error">
                {syncResult.error}
              </Alert>
            )}
            <Box mt={2} display="flex" gap={2} flexWrap="wrap">
              <Chip 
                label={`Total: ${syncResult.stats.total}`} 
                color="default"
              />
              <Chip 
                label={`Creados: ${syncResult.stats.created}`} 
                color="success"
              />
              <Chip 
                label={`Actualizados: ${syncResult.stats.updated}`} 
                color="info"
              />
              <Chip 
                label={`Desactivados: ${syncResult.stats.deactivated}`} 
                color="warning"
              />
              <Chip 
                label={`Errores: ${syncResult.stats.errors}`} 
                color={syncResult.stats.errors > 0 ? "error" : "default"}
              />
            </Box>
            {syncResult.stats.error_details && syncResult.stats.error_details.length > 0 && (
              <Accordion sx={{ mt: 2 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>Ver detalles de errores ({syncResult.stats.error_details.length})</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Código</TableCell>
                          <TableCell>Error</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {syncResult.stats.error_details.map((error, index) => (
                          <TableRow key={index}>
                            <TableCell>{error.codigo}</TableCell>
                            <TableCell>{error.error}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            )}
          </CardContent>
        </Card>
      )}

      {/* Resultados de validación */}
      {validation && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Validación de Estructura
            </Typography>
            
            {validation.valid ? (
              <Alert severity="success">
                Estructura válida - {validation.total_rows} filas encontradas
              </Alert>
            ) : (
              <Alert severity="error">
                {validation.error}
              </Alert>
            )}
            
            {validation.found_columns && (
              <Box mt={2}>
                <Typography variant="subtitle2" gutterBottom>
                  Columnas reconocidas:
                </Typography>
                <Box display="flex" gap={1} flexWrap="wrap">
                  {validation.found_columns.map((col, index) => (
                    <Chip key={index} label={col} color="success" size="small" />
                  ))}
                </Box>
              </Box>
            )}
            
            {validation.unknown_columns && validation.unknown_columns.length > 0 && (
              <Box mt={2}>
                <Typography variant="subtitle2" gutterBottom>
                  Columnas no reconocidas:
                </Typography>
                <Box display="flex" gap={1} flexWrap="wrap">
                  {validation.unknown_columns.map((col, index) => (
                    <Chip key={index} label={col} color="default" size="small" />
                  ))}
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Preview de productos */}
      {preview && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Previsualización ({preview.total_found} productos encontrados)
            </Typography>
            
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Código</TableCell>
                    <TableCell>SKU</TableCell>
                    <TableCell>Color</TableCell>
                    <TableCell>Grupo</TableCell>
                    <TableCell>Categoría</TableCell>
                    <TableCell>Descripción</TableCell>
                    <TableCell>Precio Unitario</TableCell>
                    <TableCell>Precio x m2</TableCell>
                    <TableCell>Descuento (%)</TableCell>
                    <TableCell>Stock</TableCell>
                    <TableCell>Dimensiones</TableCell>
                    <TableCell>Ancho Útil</TableCell>
                    <TableCell>Estructura m2</TableCell>
                    <TableCell>Activo</TableCell>
                    <TableCell>Moneda</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {preview.preview.map((product, index) => (
                    <TableRow key={index}>
                      <TableCell>{product.codigo}</TableCell>
                      <TableCell>{product.sku || '-'}</TableCell>
                      <TableCell>{product.color || '-'}</TableCell>
                      <TableCell>{product.grupo || '-'}</TableCell>
                      <TableCell>{product.categoria || '-'}</TableCell>
                      <TableCell>{product.descripcion || '-'}</TableCell>
                      <TableCell>{product.precio_unitario ?? '-'}</TableCell>
                      <TableCell>{product.precio_m2 ?? '-'}</TableCell>
                      <TableCell>{product.descuento ?? '-'}</TableCell>
                      <TableCell>{product.stock ?? '-'}</TableCell>
                      <TableCell>
                        {product.largo && product.ancho ? 
                          `${product.largo}×${product.ancho}` + 
                          (product.espesor ? `×${product.espesor}m` : 'm') 
                          : '-'
                        }
                      </TableCell>
                      <TableCell>{product.ancho_util ?? '-'}</TableCell>
                      <TableCell>{product.estructura_m2 || '-'}</TableCell>
                      <TableCell>{product.is_active ? 'Sí' : 'No'}</TableCell>
                      <TableCell>{product.currency || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

        </TabPanel>

        {/* Panel de Instalaciones */}
        <TabPanel value={tabValue} index={1}>
          {/* Configuración instalaciones */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Configuración de la Hoja de Instalaciones
              </Typography>
              
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Estructura esperada de la hoja:
                </Typography>
                <Typography variant="body2">
                  <strong>Trabajo</strong> | <strong>Descripcion</strong> | <strong>Unidad</strong> | <strong>Standard</strong> | <strong>Compleja</strong>
                </Typography>
                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                  • Solo la columna "Trabajo" es obligatoria<br/>
                  • Las columnas "Descripcion" y "Unidad" son opcionales<br/>
                  • Los precios pueden incluir símbolos de moneda (USD se detecta automáticamente)<br/>
                  • Formatos aceptados: $1,200.50, 1.200,50, USD 1200, etc.
                </Typography>
              </Alert>
              
              <Box display="flex" flexDirection="column" gap={2}>
                <TextField
                  label="ID de Google Sheets"
                  value={installationSpreadsheetId}
                  onChange={(e) => handleInstallationSpreadsheetIdChange(e.target.value)}
                  fullWidth
                  placeholder="1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgvE2upms"
                  helperText="El ID se encuentra en la URL: https://docs.google.com/spreadsheets/d/[ID]/edit"
                />
                
                <TextField
                  label="Rango"
                  value={installationRangeName}
                  onChange={(e) => handleInstallationRangeNameChange(e.target.value)}
                  placeholder="Sheet1!A1:E100"
                  helperText="Ejemplo: Sheet1!A1:E100 o 'Instalaciones'!A1:E100"
                />
                
                <TextField
                  label="Fila de encabezados"
                  type="number"
                  value={installationHeaderRow}
                  onChange={(e) => handleInstallationHeaderRowChange(parseInt(e.target.value) || 0)}
                  helperText="Número de fila que contiene los nombres de columnas (empezando desde 0)"
                />
              </Box>
            </CardContent>
          </Card>

          {/* Acciones instalaciones */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Acciones
              </Typography>
              <Box display="flex" gap={2} flexWrap="wrap">
                <Button
                  variant="outlined"
                  startIcon={<CheckIcon />}
                  onClick={validateInstallationSheet}
                  disabled={loading || !authStatus?.authenticated}
                >
                  1. Validar Estructura
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<PreviewIcon />}
                  onClick={previewInstallations}
                  disabled={loading || !installationValidation?.valid}
                >
                  2. Previsualizar
                </Button>
                <Button
                  variant="contained"
                  startIcon={<SyncIcon />}
                  onClick={syncInstallations}
                  disabled={loading || !installationValidation?.valid}
                  color={installationDryRun ? "primary" : "warning"}
                >
                  3. {installationDryRun ? 'Simular' : 'Sincronizar'}
                </Button>
              </Box>
              <Box mt={2}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={installationDryRun}
                      onChange={(e) => setInstallationDryRun(e.target.checked)}
                    />
                  }
                  label="Modo simulación (dry run)"
                />
              </Box>
            </CardContent>
          </Card>

          {/* Mostrar loading */}
          {loading && (
            <Box display="flex" justifyContent="center" my={3}>
              <CircularProgress />
            </Box>
          )}

          {/* Resultados de sincronización instalaciones */}
          {installationSyncResult && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {installationDryRun ? 'Simulación' : 'Resultado'} de Sincronización de Instalaciones
                </Typography>
                
                {installationSyncResult.errors.length === 0 ? (
                  <Alert severity="success">
                    {installationDryRun ? 'Simulación completada exitosamente' : 'Sincronización exitosa'}
                  </Alert>
                ) : (
                  <Alert severity="warning">
                    Completado con algunos errores. Revisa los detalles abajo.
                  </Alert>
                )}
                
                <Box mt={2} display="flex" gap={2} flexWrap="wrap">
                  <Chip label={`Procesados: ${installationSyncResult.processed}`} />
                  <Chip label={`Creados: ${installationSyncResult.created}`} color="success" />
                  <Chip label={`Actualizados: ${installationSyncResult.updated}`} color="info" />
                  {installationSyncResult.deactivated !== undefined && installationSyncResult.deactivated > 0 && (
                    <Chip label={`Desactivados: ${installationSyncResult.deactivated}`} color="warning" />
                  )}
                  <Chip label={`Errores: ${installationSyncResult.errors.length}`} color="error" />
                  {installationDryRun && (
                    <Chip label="SIMULACIÓN" color="warning" variant="outlined" />
                  )}
                </Box>
                
                {installationSyncResult.errors.length > 0 && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      <strong>Filas con problemas:</strong>
                    </Typography>
                    <Box component="ul" sx={{ pl: 2, mt: 1, mb: 0 }}>
                      {installationSyncResult.errors.map((error, index) => (
                        <Typography component="li" key={index} variant="body2" sx={{ mb: 0.5 }}>
                          {error}
                        </Typography>
                      ))}
                    </Box>
                    <Typography variant="body2" sx={{ mt: 2, fontStyle: 'italic' }}>
                      Corrige estos problemas en la hoja de Google Sheets y vuelve a sincronizar.
                    </Typography>
                  </Alert>
                )}

                {installationDryRun && installationSyncResult.errors.length === 0 && installationSyncResult.processed > 0 && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    <Typography variant="body2">
                      ✅ <strong>Todo listo para sincronizar:</strong> Se procesarán {installationSyncResult.processed} registros
                      ({installationSyncResult.created} nuevos, {installationSyncResult.updated} actualizaciones
                      {installationSyncResult.deactivated && installationSyncResult.deactivated > 0 ? `, ${installationSyncResult.deactivated} desactivaciones` : ''}). 
                      Desactiva "Modo de Prueba" y haz clic en "Sincronizar" para aplicar los cambios.
                    </Typography>
                  </Alert>
                )}

                {installationSyncResult.errors.length > 0 && installationSyncResult.processed > 0 && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    <Typography variant="body2">
                      ⚠️ <strong>Sincronización parcial:</strong> Se procesaron {installationSyncResult.processed} de las filas válidas, 
                      pero {installationSyncResult.errors.length} filas tuvieron problemas y fueron omitidas.
                    </Typography>
                  </Alert>
                )}
              </CardContent>
            </Card>
          )}

          {/* Resultados de validación instalaciones */}
          {installationValidation && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Validación de Estructura - Instalaciones
                </Typography>
                
                {installationValidation.valid ? (
                  <Alert severity="success">
                    Estructura válida - {installationValidation.total_rows} filas encontradas
                  </Alert>
                ) : (
                  <Alert severity="error">
                    {installationValidation.error}
                  </Alert>
                )}
                
                {installationValidation.found_headers && (
                  <Box mt={2}>
                    <Typography variant="subtitle2" gutterBottom>
                      Columnas encontradas:
                    </Typography>
                    <Box display="flex" gap={1} flexWrap="wrap">
                      {Object.keys(installationValidation.found_headers).map((header, index) => (
                        <Chip 
                          key={index} 
                          label={`${header} (col ${installationValidation.found_headers![header]})`} 
                          color="success" 
                          size="small" 
                        />
                      ))}
                    </Box>
                  </Box>
                )}
                
                {installationValidation.missing_headers && installationValidation.missing_headers.length > 0 && (
                  <Box mt={2}>
                    <Typography variant="subtitle2" gutterBottom>
                      Columnas faltantes (requeridas):
                    </Typography>
                    <Box display="flex" gap={1} flexWrap="wrap">
                      {installationValidation.missing_headers.map((header, index) => (
                        <Chip key={index} label={header} color="error" size="small" />
                      ))}
                    </Box>
                  </Box>
                )}
              </CardContent>
            </Card>
          )}

          {/* Preview de instalaciones */}
          {installationPreview && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Previsualización ({installationPreview.total_data_rows} filas de datos encontradas)
                </Typography>
                
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Trabajo</TableCell>
                        <TableCell>Descripción</TableCell>
                        <TableCell>Unidad</TableCell>
                        <TableCell>Precio Standard</TableCell>
                        <TableCell>Precio Complejo</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {installationPreview.preview.map((installation, index) => (
                        <TableRow key={index}>
                          <TableCell>{installation.Trabajo || '-'}</TableCell>
                          <TableCell>{installation.Descripcion || '-'}</TableCell>
                          <TableCell>{installation.Unidad || '-'}</TableCell>
                          <TableCell>{installation.Standard || '-'}</TableCell>
                          <TableCell>{installation.Compleja || '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}
        </TabPanel>

        {/* Panel de Condiciones */}
        <TabPanel value={tabValue} index={2}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Condiciones por Defecto para Presupuestos
              </Typography>
              
              <Typography variant="body2" color="text.secondary" paragraph>
                Define las condiciones generales y adicionales que se cargarán por defecto en nuevos presupuestos.
              </Typography>

              {conditionsSaved && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  ✓ Condiciones guardadas exitosamente
                </Alert>
              )}

              <Box display="flex" flexDirection="column" gap={3}>
                {/* Condiciones Generales */}
                <Box>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                    Condiciones Generales
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={6}
                    value={conditions.condiciones_generales_default}
                    onChange={(e) => setConditions({
                      ...conditions,
                      condiciones_generales_default: e.target.value
                    })}
                    placeholder="Ingresa las condiciones generales de contratación..."
                    disabled={conditionsLoading}
                    variant="outlined"
                  />
                </Box>

                {/* Condiciones Adicionales */}
                <Box>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                    Condiciones Adicionales
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={6}
                    value={conditions.condiciones_adicionales_default}
                    onChange={(e) => setConditions({
                      ...conditions,
                      condiciones_adicionales_default: e.target.value
                    })}
                    placeholder="Ingresa las condiciones adicionales..."
                    disabled={conditionsLoading}
                    variant="outlined"
                  />
                </Box>

                {/* Botones de acción */}
                <Box display="flex" gap={2}>
                  <Button
                    variant="contained"
                    onClick={saveConditions}
                    disabled={conditionsLoading}
                    startIcon={conditionsLoading ? <CircularProgress size={20} /> : <SyncIcon />}
                  >
                    {conditionsLoading ? 'Guardando...' : 'Guardar Condiciones'}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={loadConditions}
                    disabled={conditionsLoading}
                  >
                    Recargar
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>

          <Alert severity="info">
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
              💡 Cómo se usan estas condiciones:
            </Typography>
            <Typography variant="body2">
              Cuando un usuario crea un nuevo presupuesto, estos textos se cargan automáticamente en los campos correspondientes. 
              El usuario puede modificarlos según sea necesario para ese presupuesto específico.
            </Typography>
          </Alert>
        </TabPanel>
      </Card>
    </Box>
  );
};

export default GoogleSheetsSync;
